import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, apiWithETag } from '../../lib/api';
import { modeLabel, reasonLabel, regimeLabel } from '../../lib/labels';
import type { Draft, PublishResult, Site } from '../../lib/types';
import {
  Badge, Banner, Button, Card, CopyButton, Field, Icon, LoadError, Skeleton, input,
} from '../../lib/ui';
import { snippet } from './SetupChecklist';
import { t } from '../../lib/i18n';

/**
 * From nothing to a banner that is actually live.
 *
 * The old form asked for a name and a domain and dropped you on a dashboard reading
 * "0,0 %". Everything that decides whether the thing works — the other domains the site
 * is reachable under, which law applies, what the banner says, whether it is published,
 * whether the snippet is in — happened afterwards, in four different places, in whatever
 * order you happened to find them.
 *
 * The site is created at the end of step 1 and every later step saves as it goes. Leaving
 * halfway therefore leaves a real site rather than a lost form, and the setup checklist
 * picks up exactly where the wizard stopped.
 */
type Step = 'site' | 'domains' | 'law' | 'text' | 'live';

// German labels are the keys; translated in the stepper below.
const STEPS: [Step, string][] = [
  ['site', 'Website'],
  ['domains', 'Domains'],
  ['law', 'Rechtslage'],
  ['text', 'Banner-Text'],
  ['live', 'Live schalten'],
];

interface Effective {
  regime: {
    resolved: string;
    mode: string;
    require_reject_button: boolean;
    consent_lifetime_days: number;
  };
  reason: string;
}

export function NewSiteWizard({ onDone, onCancel }: {
  onDone: (site: Site) => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('site');
  const [site, setSite] = useState<Site | null>(null);

  const at = STEPS.findIndex(([s]) => s === step);

  return (
    <Card
      title={t('Neue Website')}
      actions={
        <span className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
          {t('Schritt {i} von {n}', { i: at + 1, n: STEPS.length })}
          <Button variant="ghost" size="sm" onClick={() => (site ? onDone(site) : onCancel())}>
            {site ? t('Später weitermachen') : t('Abbrechen')}
          </Button>
        </span>
      }
    >
      {/* The steps are shown, not just counted: what is still coming is what tells someone
          whether this is worth starting now. Completed ones are clickable, upcoming ones
          are not — a step nobody has reached yet has nothing to show. */}
      <ol className="mb-5 flex flex-wrap items-center gap-x-1 gap-y-2 text-xs">
        {STEPS.map(([s, label], i) => {
          const done = i < at;
          const here = i === at;
          return (
            <li key={s} className="flex items-center gap-1">
              {i > 0 && <span className="mr-1 text-[var(--color-line)]">·</span>}
              <button
                disabled={!done}
                onClick={() => setStep(s)}
                className={`inline-flex items-center gap-1 whitespace-nowrap rounded-[3px]
                            px-2 py-1 transition-colors ${
                  here
                    ? 'bg-[var(--color-sunken)] font-medium text-[var(--color-ink)]'
                    : done
                      ? 'text-[var(--color-brand)] hover:bg-[var(--color-sunken)]'
                      : 'text-[var(--color-muted)]'
                }`}
              >
                {done && <Icon name="check" size={11} />}
                {t(label)}
              </button>
            </li>
          );
        })}
      </ol>

      {step === 'site' && (
        <SiteStep
          onCreated={(s) => {
            void qc.invalidateQueries({ queryKey: ['sites'] });
            setSite(s);
            setStep('domains');
          }}
        />
      )}
      {step === 'domains' && site && (
        <DomainsStep site={site} onNext={() => setStep('law')} />
      )}
      {step === 'law' && site && <LawStep site={site} onNext={() => setStep('text')} />}
      {step === 'text' && site && <TextStep site={site} onNext={() => setStep('live')} />}
      {step === 'live' && site && <LiveStep site={site} onDone={() => onDone(site)} />}
    </Card>
  );
}

// ── 1. Name and primary domain ───────────────────────────────────────────────────────

function SiteStep({ onCreated }: { onCreated: (s: Site) => void }) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [touchedName, setTouchedName] = useState(false);

  // Typing the domain first is the common order, and the name is almost always the domain
  // without its suffix. Derived from the cleaned host, not from the raw field: people
  // paste whole URLs out of a browser bar, and deriving from that produced "Https://shop".
  useEffect(() => {
    if (touchedName) return;
    const base = clean(domain).replace(/^www\./, '').split('.')[0] ?? '';
    setName(base ? base.charAt(0).toUpperCase() + base.slice(1) : '');
  }, [domain, touchedName]);

  const create = useMutation({
    mutationFn: () =>
      api<Site>('/v1/sites', {
        method: 'POST',
        body: { name: name.trim(), primary_domain: clean(domain), domains: [] },
      }),
    onSuccess: onCreated,
  });

  const fieldErr = (f: string): string | undefined => {
    if (!(create.error instanceof ApiError)) return undefined;
    const code = create.error.problem.errors?.find((e) => e.field === f)?.code;
    return code === 'taken' ? t('Diese Domain gehört bereits zu einer anderen Website.')
      : code === 'pattern' || code === 'invalid' ? t('Bitte nur die Domain, ohne https:// und ohne Pfad.')
      : code === 'required' || code === 'length' ? t('Pflichtfeld.')
      : undefined;
  };

  return (
    <div className="space-y-4">
      <Field label={t('Domain')} hint={t('Ohne https:// und ohne Pfad — nur der Host.')}
        error={fieldErr('primary_domain')}>
        {/* Normalised when the field is left, not while typing: the hint says "ohne
            https://" and the field then silently accepted it anyway, so what would be
            stored and what was on screen were two different strings. */}
        <input className={input} value={domain} placeholder="kunde.de" autoFocus
          onChange={(e) => setDomain(e.target.value)}
          onBlur={() => setDomain(clean(domain))} />
      </Field>
      <Field label={t('Name')} hint={t('Nur für Sie. Taucht auf der Website nicht auf.')}
        error={fieldErr('name')}>
        <input className={input} value={name} placeholder={t('Kundenshop')}
          onChange={(e) => { setTouchedName(true); setName(e.target.value); }} />
      </Field>

      {create.isError && !fieldErr('name') && !fieldErr('primary_domain') && (
        <Banner kind="error">{t('Anlegen fehlgeschlagen.')}</Banner>
      )}

      <Button disabled={!name.trim() || !clean(domain) || create.isPending}
        onClick={() => create.mutate()}>
        {create.isPending ? t('Wird angelegt …') : t('Weiter')}
      </Button>
    </div>
  );
}

/** Strips what people paste out of a browser bar. The server validates the rest. */
const clean = (v: string): string =>
  v.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/:\d+$/, '');

// ── 2. The other hosts the site answers on ───────────────────────────────────────────

function DomainsStep({ site, onNext }: { site: Site; onNext: () => void }) {
  const primary = site.primary_domain;
  const isWww = primary.startsWith('www.');
  const twin = isWww ? primary.slice(4) : `www.${primary}`;

  const [rows, setRows] = useState<{ domain: string; test: boolean }[]>([
    { domain: twin, test: false },
  ]);
  const [problem, setProblem] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      for (const r of rows) {
        const d = clean(r.domain);
        if (!d) continue;
        await api(`/v1/sites/${site.id}/domains`, {
          method: 'POST',
          body: { domain: d, is_test: r.test },
        });
      }
    },
    onSuccess: onNext,
    onError: () => setProblem(
      t('Mindestens eine Domain wurde nicht übernommen. Sie können sie später unter „Blockieren & Anbieter“ nachtragen.'),
    ),
  });

  return (
    <div className="space-y-4">
      {/* The single most common support case is a banner that does not appear because the
          host is not on the allowlist — www against non-www, or a staging domain. Asking
          here costs one screen; finding out later costs a support call. */}
      <p className="text-[13px] text-[var(--color-muted)]">
        {t('Nur eingetragene Hosts dürfen das Banner laden. {primary} ist gesetzt. Fehlt {twin} oder eine Testumgebung, erscheint dort kein Banner — das ist mit Abstand die häufigste Ursache dafür, dass es „nicht funktioniert“.',
          { primary, twin: isWww ? t('die Variante ohne www') : t('die www-Variante') })}
      </p>

      <ul className="space-y-2">
        {rows.map((r, i) => (
          <li key={i} className="flex flex-wrap items-center gap-3">
            <input className={`${input} min-w-[220px] flex-1`} value={r.domain}
              placeholder="staging.kunde.de" aria-label={t('Weitere Domain {n}', { n: i + 1 })}
              onChange={(e) => setRows(rows.map((x, n) => n === i ? { ...x, domain: e.target.value } : x))} />
            <label className="flex items-center gap-1.5 text-[13px]">
              <input type="checkbox" checked={r.test}
                onChange={(e) => setRows(rows.map((x, n) => n === i ? { ...x, test: e.target.checked } : x))} />
              {t('Testumgebung')}
            </label>
            <button className="text-xs text-[var(--color-muted)] hover:underline"
              onClick={() => setRows(rows.filter((_, n) => n !== i))}>
              {t('Entfernen')}
            </button>
          </li>
        ))}
      </ul>

      <button className="text-xs text-[var(--color-brand)] hover:underline"
        onClick={() => setRows([...rows, { domain: '', test: true }])}>
        {t('Weitere Domain')}
      </button>

      {problem && <Banner kind="error">{problem}</Banner>}

      <div className="flex gap-2">
        <Button disabled={add.isPending} onClick={() => add.mutate()}>
          {add.isPending ? t('Wird gespeichert …') : t('Weiter')}
        </Button>
        <Button variant="ghost" onClick={onNext}>{t('Überspringen')}</Button>
      </div>
    </div>
  );
}

// ── 3. Which law applies ─────────────────────────────────────────────────────────────

function LawStep({ site, onNext }: { site: Site; onNext: () => void }) {
  const eff = useQuery({
    queryKey: ['effective', site.id],
    queryFn: () => api<Effective>(`/v1/sites/${site.id}/rules/effective`),
  });

  return (
    <div className="space-y-4">
      {/* Read-only on purpose. The regime follows the visitor's country and the tenant
          default; a per-site override is a guarded operation (Doc 00 §6) and does not
          belong in a setup flow, where it would be the first thing someone weakens
          without knowing what they were weakening. */}
      <p className="text-[13px] text-[var(--color-muted)]">
        {t('Das gilt für Besucher, deren Land sich nicht bestimmen lässt. Für alle anderen entscheidet ihr Aufenthaltsort — strenger, nie lockerer.')}
      </p>

      {eff.isPending && <Skeleton rows={2} />}
      {eff.isError && <LoadError what={t('Die Rechtslage')} onRetry={() => void eff.refetch()} />}
      {eff.data && (
        <div className="flex flex-wrap gap-x-8 gap-y-3 rounded-[3px] border border-[var(--color-line)] p-3">
          <Fact label={t('Regime')} value={regimeLabel(eff.data.regime.resolved)} />
          <Fact label={t('Grundlage')} value={modeLabel(eff.data.regime.mode)} />
          <Fact label={t('Ablehnen-Button')}
            value={eff.data.regime.require_reject_button ? t('Verpflichtend') : t('Optional')} />
          <Fact label={t('Einwilligung gilt')}
            value={t('{n} Tage', { n: eff.data.regime.consent_lifetime_days })} />
          <Fact label={t('Ermittelt aus')} value={reasonLabel(eff.data.reason)} />
        </div>
      )}

      <Button onClick={onNext}>{t('Weiter')}</Button>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="mt-0.5 text-[13px] font-medium">{value}</div>
    </div>
  );
}

// ── 4. What the banner says ──────────────────────────────────────────────────────────

function TextStep({ site, onNext }: { site: Site; onNext: () => void }) {
  const loaded = useQuery({
    queryKey: ['draft', site.id],
    queryFn: () => apiWithETag<Draft>(`/v1/sites/${site.id}/config`),
  });
  const [title, setTitle] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const lang = loaded.data?.data.i18n_available[0] ?? 'de';
  const draft = loaded.data?.data;

  // Resolved once, and used both for what is shown and for what is saved. Reading the
  // draft's wording only in the input's `value` meant an untouched form displayed the
  // default text and saved two empty strings — which the server correctly refused as a
  // missing translation, on a field the user had never touched.
  // Not `t`: that is the translation function.
  const titleValue = title ?? draft?.texts[lang]?.title ?? '';
  const b = body ?? draft?.texts[lang]?.body ?? '';

  const save = useMutation({
    mutationFn: () => {
      const next: Draft = {
        ...draft!,
        texts: { ...draft!.texts, [lang]: { ...draft!.texts[lang], title: titleValue, body: b } },
      };
      return api(`/v1/sites/${site.id}/config`, { method: 'PUT', body: next });
    },
    onSuccess: onNext,
    // The server names the field it rejected; a bare "konnte nicht gespeichert werden"
    // throws that away and leaves someone re-typing a title that was never the problem.
    onError: (e) => setProblem(
      e instanceof ApiError && e.problem.errors?.length
        ? t('Abgelehnt: {details}',
            { details: e.problem.errors.map((x) => `${x.field} (${x.code})`).join(', ') })
        : t('Der Text konnte nicht gespeichert werden.'),
    ),
  });

  if (loaded.isPending) return <Skeleton rows={4} />;
  if (loaded.isError || !draft) {
    return <LoadError what={t('Der Entwurf')} onRetry={() => void loaded.refetch()} />;
  }

  return (
    <div className="space-y-4">
      {/* Prefilled, not blank. A blank consent notice is not a starting point, it is a
          screen someone will publish unread — and this wording lands verbatim in every
          consent proof. */}
      <p className="text-[13px] text-[var(--color-muted)]">
        {t('Dieser Wortlaut wird bei jeder Einwilligung mitgespeichert. Alles Weitere — Farben, Kategorien, Anbieter — steht danach im Editor.')}
      </p>

      <Field label={t('Überschrift')}>
        <input className={input} value={titleValue} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label={t('Text')}>
        <textarea className={`${input} min-h-[96px]`} value={b}
          onChange={(e) => setBody(e.target.value)} />
      </Field>

      {problem && <Banner kind="error">{problem}</Banner>}

      <Button disabled={!titleValue.trim() || !b.trim() || save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? t('Wird gespeichert …') : t('Weiter')}
      </Button>
    </div>
  );
}

// ── 5. Publish and install ───────────────────────────────────────────────────────────

interface Install {
  loader_found: boolean;
  site_key_matches: boolean;
  banner_rendered: boolean;
  trackers_before_consent: string[];
  error: string | null;
}

function LiveStep({ site, onDone }: { site: Site; onDone: () => void }) {
  const qc = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const [published, setPublished] = useState(false);

  const publish = useMutation({
    mutationFn: () => api<PublishResult>(`/v1/sites/${site.id}/config/publish`,
      { method: 'POST', body: { note: 'Ersteinrichtung' } }),
    onSuccess: () => {
      setPublished(true);
      setProblem(null);
      void qc.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e) => setProblem(
      e instanceof ApiError && e.status === 422
        ? t('Die Konfiguration hat noch offene Probleme. Der Editor zeigt welche.')
        : e instanceof ApiError && e.status === 403
          ? t('Ihre Rolle darf nicht veröffentlichen. Bitten Sie Admin oder Inhaber darum.')
          : t('Veröffentlichen fehlgeschlagen.'),
    ),
  });

  const check = useMutation({
    mutationFn: () => api<Install>(`/v1/sites/${site.id}/install`, { method: 'POST' }),
  });
  const inst = check.data;

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-1 text-[13px] font-medium">{t('1. Veröffentlichen')}</div>
        <p className="mb-2 text-xs text-[var(--color-muted)]">
          {/* Until this happens the CDN answers 404 and the SDK falls back to its built-in
              rules — the snippet would be installed and enforcing something nobody chose. */}
          {t('Solange nichts veröffentlicht ist, liefert das CDN nichts aus und das SDK arbeitet mit seinen eingebauten Notfall-Regeln.')}
        </p>
        {published ? (
          <Badge tone="live"><Icon name="check" size={12} /> {t('Veröffentlicht')}</Badge>
        ) : (
          <Button variant="publish" disabled={publish.isPending} onClick={() => publish.mutate()}>
            {publish.isPending ? t('Wird veröffentlicht …') : t('Jetzt veröffentlichen')}
          </Button>
        )}
      </div>

      <div className="border-t border-[var(--color-line)] pt-4">
        <div className="mb-1 flex items-center justify-between gap-3">
          <span className="text-[13px] font-medium">{t('2. Snippet einbauen')}</span>
          <CopyButton value={snippet(site.site_key)} />
        </div>
        <pre className="overflow-x-auto rounded-[3px] border border-[var(--color-line)] bg-[var(--color-paper)] p-3 text-xs leading-relaxed">
{snippet(site.site_key)}
        </pre>
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          {t('So weit oben im <head> wie möglich. Alles, was davor steht, kann das SDK nicht mehr anhalten.')}
        </p>
      </div>

      <div className="border-t border-[var(--color-line)] pt-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="text-[13px] font-medium">{t('3. Prüfen')}</span>
          <Button variant="secondary" size="sm" disabled={check.isPending}
            onClick={() => check.mutate()}>
            {check.isPending ? t('Wird geprüft …') : t('Einbau prüfen')}
          </Button>
        </div>
        {inst && (
          <Banner kind={inst.loader_found && inst.site_key_matches ? 'ok' : 'error'}>
            {inst.error
              ? t('Die Seite war nicht erreichbar: {error}', { error: inst.error })
              : !inst.loader_found
                ? t('Auf der Startseite wurde kein Loader gefunden.')
                : !inst.site_key_matches
                  ? t('Es lädt ein Loader, aber mit einem anderen Site-Schlüssel.')
                  : inst.trackers_before_consent.length > 0
                    ? t('Trotz Banner wurden vor der Einwilligung Anbieter geladen: {vendors}. Meist steht das Snippet zu weit unten.',
                        { vendors: inst.trackers_before_consent.join(', ') })
                    : t('Der Loader wurde gefunden und das Banner ist erschienen.')}
          </Banner>
        )}
      </div>

      {problem && <Banner kind="error">{problem}</Banner>}

      <div className="border-t border-[var(--color-line)] pt-4">
        <Button onClick={onDone}>{t('Fertig — zur Website')}</Button>
      </div>
    </div>
  );
}
